import { HostContext } from "@buzzeasy/shared-frontend-utilities";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TranscriptHubContext } from "../components/providers/TranscriptHubProvider";
import { mapToSimplifiedItem, QuickResponse } from "../model/nextBestAction";
import { useGetAgentAssistConfigQuery, useLazyGenerateShortcutAnswerQuery } from "../redux/apis/agentCopilotApi";
import { RootState } from "../redux/store";

export interface UseQuickResponsesReturn {
  quickResponses: QuickResponse[];
  processingQuickResponseId: string | null;
  sendQuickResponse: (responseId: string) => Promise<void>;
  isReactive: boolean;
  isSendingQuickResponse: boolean;
  canSendQuickResponses: boolean;
}

export default function useQuickResponses(): UseQuickResponsesReturn {
  const { messageApi } = useContext(HostContext);
  const { transcriptItems, sendTextMessage } = useContext(TranscriptHubContext);
  const { t } = useTranslation();

  const { copilotId } = useSelector((state: RootState) => state.agentCopilotAccess);

  const { data: agentAssistConfig } = useGetAgentAssistConfigQuery(undefined, { skip: !copilotId });
  const [getGenerateShortcutAnswer] = useLazyGenerateShortcutAnswerQuery();

  const quickResponses = useMemo(
    () => agentAssistConfig?.shortcuts ?? [],
    [agentAssistConfig],
  );

  const isReactive = useMemo(
    () => agentAssistConfig?.isReactive ?? false,
    [agentAssistConfig],
  );

  const [processingQuickResponseId, setProcessingQuickResponseId] = useState<string | null>(null);

  const sendQuickResponse = useCallback(
    async (quickResponseId: string) => {
      setProcessingQuickResponseId(quickResponseId);

      try {
        const simplifiedItems = transcriptItems.map(mapToSimplifiedItem);
        const result = await getGenerateShortcutAnswer({ data: { quickResponseId, transcript: simplifiedItems } }).unwrap();

        sendTextMessage(result.answer);
        setProcessingQuickResponseId("");
        setTimeout(() => setProcessingQuickResponseId(null), 3000);
      }
      catch {
        setProcessingQuickResponseId(null);
        messageApi.error({ content: t("notifications.failedToGenerateQuickResponse") });
      }
    },
    [getGenerateShortcutAnswer, messageApi, sendTextMessage, t, transcriptItems],
  );

  const isSendingQuickResponse = processingQuickResponseId !== null;
  const canSendQuickResponses = !!copilotId && quickResponses.length > 0 && transcriptItems.length > 0;

  return useMemo<UseQuickResponsesReturn>(
    () => ({
      quickResponses,
      processingQuickResponseId,
      sendQuickResponse,
      isReactive,
      isSendingQuickResponse,
      canSendQuickResponses,
    }),
    [quickResponses, processingQuickResponseId, sendQuickResponse, isReactive, isSendingQuickResponse, canSendQuickResponses],
  );
}
